/**
 * Create args for a filter derived from a combobox selection.
 * @param value
 */
export function createComboboxFilterArgs(value, allValue) {
  return value === allValue ? {
    operator: 'notEq',
    value: ''
  } // this is synonymous with "no filter"
  : {
    operator: 'eq',
    value
  };
}

/**
 * Create a filter condition factory for a `contains` filter that matches the
 * given search text.
 * @param tableUtils TableUtils instance to create filter from
 * @param columnNames Names of the columns to filter
 * @param searchText Text to search (will be trimmed of leading / trailing whitespace)
 * @param timeZone Timezone to use for date parsing
 */
export function createSearchTextFilter(tableUtils, columnNames, searchText, timeZone) {
  /**
   * Creates a filter condition that matches based on search text.
   * @param maybeTable Table to filter
   */
  return function searchTextFilter(maybeTable) {
    var searchTextTrimmed = searchText.trim();
    if (searchTextTrimmed === '') {
      return null;
    }
    var factory = createFilterConditionFactory(columnNames, col => tableUtils.makeSearchTextFilter(col, searchTextTrimmed, timeZone), 'or');
    return factory(maybeTable);
  };
}

/**
 * Create a filter condition factory function.
 * @param columnNames Column names that filter conditions will target
 * @param createColumnCondition Function that can create a `FilterCondition` from
 * a given column. This will be run across all columns corresponding to the given
 * list of column names.
 * @param conditionOperator Operator that will be used to combine multiple
 * `FilterConditions` if multiple column names are given.
 */
export function createFilterConditionFactory(columnNames, createColumnCondition) {
  var conditionOperator = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 'or';
  return function filterConditionFactory(maybeTable) {
    var maybeColumns = maybeTable === null || maybeTable === void 0 ? void 0 : maybeTable.findColumns(typeof columnNames === 'string' ? [columnNames] : columnNames);
    if (maybeColumns == null || maybeColumns.length === 0) {
      return null;
    }
    var filterConditions = maybeColumns.map(createColumnCondition);
    return filterConditions.reduce((current, next) => current[conditionOperator](next));
  };
}

/**
 * Create a filter condition factory for a filter operator that matches the
 * given value.
 * @param tableUtils TableUtils instance to create filter from
 * @param columnNames Column names to compare value to
 * @param value Value to match
 * @param operator Operator to use for matching
 */
export function createValueFilter(tableUtils, columnNames, value, operator) {
  /**
   * Creates a filter condition that matches based on matching a given value.
   * @param maybeTable Table to filter
   */
  return createFilterConditionFactory(columnNames, col => col.filter()[operator](tableUtils.makeFilterValue(col.type, value)), 'or');
}

/**
 * Create a filter condition factory for a filter that matches a given Selection.
 * If column is not found or selection parameter is 'all', the factory will return
 * null to indicate the results will be unfiltered.
 * @param tableUtils TableUtils instance to create filter from
 * @param columnName The column name to filter
 * @param selection 'all' or an array of values to filter by
 * @param emptySelectionEqAll If true, empty selection means select
 * all. If false, it means select none.
 * @param invertSelection  Invert the selection (eg. All items are selected,
 * then you deselect items)
 */
export function createSelectedValuesFilter(tableUtils, columnName, selection, emptySelectionEqAll, invertSelection) {
  /**
   * Creates a filter condition that matches rows for selected values. Returns
   * null to indicate no filtering.
   * @param maybeTable the table to filter
   */
  return function selectedValuesFilter(maybeTable) {
    var maybeColumn = maybeTable === null || maybeTable === void 0 ? void 0 : maybeTable.findColumn(columnName);
    var isAllSelected = selection === 'all' || emptySelectionEqAll && selection.size === 0;
    if (maybeColumn == null || isAllSelected) {
      return null;
    }
    return tableUtils.makeSelectValueFilter(maybeColumn, [...selection.keys()], invertSelection);
  };
}

/**
 * Creates a `notNullOrEmptyFilterCondition` function.
 * @param tableUtils TableUtils instance to use for making filters.
 */
export function createNotNullOrEmptyFilterCondition(tableUtils) {
  /**
   * Returns a filter condition that matches values in a given column that are
   * not null or empty string.
   */
  return function notNullOrEmptyFilterCondition(column) {
    return column.filter().isNull().not().and(column.filter().notEq(tableUtils.makeFilterValue(column.type, '')));
  };
}

/**
 * Creates a `showOnlyEmptyFilterCondition` function that can be toggled on or
 * off.
 * @param tableUtils TableUtils instance to use for making filters.
 * @param isOn Flag to indicate if the filter should be on or off.
 */
export function createShowOnlyEmptyFilterCondition(tableUtils, isOn) {
  /**
   * Returns a filter condition that matches values in a given column. If the
   * `isOn` param is true, this will filter to only null or empty string values.
   * If `isOn` is false, it will return a filter that matches all values.
   */
  return function showOnlyEmptyFilterCondition(column) {
    var filter = column.filter();
    var emptyStringValue = tableUtils.makeFilterValue(column.type, '');
    var eqEmptyStringCondition = filter.eq(emptyStringValue);
    if (isOn) {
      return filter.isNull().or(eqEmptyStringCondition);
    }

    // If filter is off, return a condition that will always be true
    return eqEmptyStringCondition.or(filter.notEq(emptyStringValue));
  };
}
//# sourceMappingURL=FilterUtils.js.map